import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * GoogleSignInButton — "Continue with Google" (custom Google Cloud OAuth 2.0).
 *
 * Flow:
 *   1. Read any pre-auth `?ref=CODE` and `?utm_source=X` from the current
 *      URL. Both are passed as query params to our backend login route,
 *      which signs them into the OAuth `state` param so referral +
 *      channel attribution survive the Google roundtrip server-side.
 *   2. Preflight `fetch(...)` with `{ redirect: 'manual' }` — if the
 *      backend replies 503 GOOGLE_AUTH_NOT_CONFIGURED, show a toast and
 *      abort. Otherwise the response is `opaqueredirect` (browser will
 *      follow) → navigate the full page.
 *   3. Google redirects back to our backend callback, which mints our JWT
 *      and 302s to `/auth/google-callback#token=<jwt>`.
 *
 * Note: pre-auth localStorage snapshot is NO LONGER USED — attribution
 * now rides in the signed `state` param end-to-end.
 */
const BACKEND_URL = (
  process.env.REACT_APP_BACKEND_URL || ""
).replace(/\/$/, "");
const REFERRAL_RE = /^MB-[A-Z2-9]{4,10}$/i;
const UTM_RE = /^[a-z0-9_-]{1,32}$/;

export function GoogleSignInButton({
  label = "Continue with Google",
  disabled = false,
  className = "",
  testId = "google-signin-button",
}) {
  const [searchParams] = useSearchParams();
  const [pending, setPending] = useState(false);

  const handleClick = async () => {
    if (pending) return;

    // Sanitize incoming ref + utm to match the backend's expectations.
    const rawRef = (searchParams.get("ref") || "").trim().toUpperCase();
    const rawSrc = (searchParams.get("utm_source") || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "")
      .slice(0, 32);
    const params = new URLSearchParams();
    if (REFERRAL_RE.test(rawRef)) params.set("ref", rawRef);
    if (UTM_RE.test(rawSrc)) params.set("utm_source", rawSrc);
    const qs = params.toString();
    const loginUrl = `${BACKEND_URL}/api/v1/auth/google/login${qs ? "?" + qs : ""}`;

    setPending(true);
    try {
      // Preflight: `redirect: 'manual'` lets us intercept a 503 without
      // browsing off to Google. An opaqueredirect (type='opaqueredirect')
      // means the backend is 302'ing correctly — proceed with full-page nav.
      const preflight = await fetch(loginUrl, { redirect: "manual" });
      if (preflight.status === 503) {
        toast.error("Google sign-in isn't configured yet — please try email sign-in.");
        return;
      }
      // Any other explicit failure (rare — CORS blocks or 5xx).
      if (
        preflight.type !== "opaqueredirect" &&
        preflight.status !== 0 &&
        !preflight.ok
      ) {
        toast.error("We couldn't start Google sign-in. Please try again.");
        return;
      }
    } catch {
      // Network / CORS failure — fall through and let the browser try
      // navigating anyway; the user will see a plain error page if the
      // backend is truly down.
    }
    // Full-page navigation — the backend will 302 to accounts.google.com.
    window.location.href = loginUrl;
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || pending}
      className={
        "mb-focus-ring mb-transition inline-flex h-12 w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-5 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60 " +
        className
      }
      data-testid={testId}
      aria-label={label}
    >
      {pending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <GoogleGlyph className="h-5 w-5" />
      )}
      <span>{label}</span>
    </button>
  );
}

/**
 * Official Google "G" mark — 4-color SVG.
 */
function GoogleGlyph({ className = "" }) {
  return (
    <svg
      viewBox="0 0 48 48"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      <path
        fill="#4285F4"
        d="M46.02 24.55c0-1.53-.14-3.01-.4-4.44H24v8.4h12.36c-.53 2.87-2.14 5.31-4.56 6.94v5.77h7.38c4.32-3.98 6.84-9.85 6.84-16.67z"
      />
      <path
        fill="#34A853"
        d="M24 47.02c6.17 0 11.34-2.05 15.12-5.55l-7.38-5.77c-2.05 1.38-4.68 2.19-7.74 2.19-5.95 0-10.99-4.02-12.79-9.42H3.61v5.92C7.36 41.99 15.03 47.02 24 47.02z"
      />
      <path
        fill="#FBBC05"
        d="M11.21 28.47c-.46-1.38-.72-2.85-.72-4.38s.26-3 .72-4.38v-5.92H3.61A22.99 22.99 0 001 24.09c0 3.71.88 7.22 2.61 10.31l7.6-5.93z"
      />
      <path
        fill="#EA4335"
        d="M24 9.51c3.35 0 6.37 1.15 8.74 3.41l6.55-6.55C35.34 2.61 30.17.98 24 .98 15.03.98 7.36 6.01 3.61 13.79l7.6 5.92C13.01 13.53 18.05 9.51 24 9.51z"
      />
    </svg>
  );
}
